import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallPwaPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("zeastro_pwa_dismissed") === "1") return;
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible || !deferred) return null;

  const install = async () => {
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    localStorage.setItem("zeastro_pwa_dismissed", "1");
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("zeastro_pwa_dismissed", "1");
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-br from-purple-900/95 to-pink-900/95 backdrop-blur-md border border-purple-500/30 rounded-2xl p-4 shadow-2xl z-50" data-testid="install-pwa-prompt">
      <button onClick={dismiss} className="absolute top-2 right-2 text-white/60 hover:text-white" data-testid="button-dismiss-pwa"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3">
        <div className="text-3xl">✨</div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white mb-1">Install ZEASTRO AI</div>
          <div className="text-xs text-white/80 mb-3">Add to home screen for instant access, push notifications, and offline reading.</div>
          <Button onClick={install} size="sm" className="bg-white text-purple-900 hover:bg-purple-50" data-testid="button-install-pwa">
            <Download className="w-3 h-3 mr-1" /> Install
          </Button>
        </div>
      </div>
    </div>
  );
}
